import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useDeleteModuleTemplateMutation } from "entities/modules/mutations/useDeleteModuleTemplateMutation";
import { useUpdateModuleTemplateStatusMutation } from "entities/modules/mutations/useUpdateModuleTemplateStatusMutation";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import React, { FC } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import SearchField from "shared/components/SearchField";
import TableWithPagination from "shared/components/TableWithPagination";
import { StyledButton } from "../tagsAndMetadataTags/pages/TagsAndMetadataTags/styled";
import ModuleDetailsDrawer from "./components/ModuleDetailsDrawer";
import { ModulesPageProvider, useModulesPage } from "./context";
import { getModulesColumns, generateTextForDeletingPopup } from "./helpers";

const ModulesContainer = () => (
  <ModulesPageProvider>
    <ModuleTemplates />
  </ModulesPageProvider>
);

const ModuleTemplates: FC = () => {
  const {
    isLoading,
    totalItemsCount,
    gridPaginationModel,
    handleSortModelChange,
    handlePaginationModelChange,
    moduleDetailsDrawer,
    selectedModuleTemplate,
    setSelectedModuleTemplate,
    openDeleteModal,
    handleDeleteModal,
    searchData,
  } = useModulesPage();

  const { mutate: updateModuleStatus, isPending } =
    useUpdateModuleTemplateStatusMutation();

  const { mutate: deleteModuleTemplate, isPending: isDeleteLoading } =
    useDeleteModuleTemplateMutation(handleDeleteModal);

  const writeAccess = useRolePermission("moduleTemplates:write");

  const columnsParams = {
    moduleDetailsDrawer: moduleDetailsDrawer,
    handleStatus: updateModuleStatus,
    handleDeleteModuleTemplate: handleDeleteModal,
    setSelectedModuleTemplate: setSelectedModuleTemplate,
    readOnly: !writeAccess.allowed,
  };

  const columns = getModulesColumns(columnsParams);

  const onDeleteModule = () => {
    selectedModuleTemplate && deleteModuleTemplate(selectedModuleTemplate?.id);
    invalidateEntireModuleListQuery();
  };

  return (
    <FlexColumn>
      <PageHeading>
        {writeAccess.allowed && (
          <StyledButton
            title="Add new module"
            size="small"
            onClick={() => moduleDetailsDrawer.openDrawer()}
          />
        )}
      </PageHeading>

      <FlexCenter marginBottom={"1rem"}>
        <SearchField
          placeholder="Search..."
          value={searchData.value}
          onChange={searchData.onChange}
        />
      </FlexCenter>

      <TableWithPagination
        loading={isLoading || isPending || isDeleteLoading}
        columns={columns}
        emptyStateMessage="No modules found"
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[25, 50, 100]}
        data={searchData.filteredData}
        rowCount={totalItemsCount}
        paginationModel={gridPaginationModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={(params) => moduleDetailsDrawer.openDrawer(params.row)}
      />
      <ModuleDetailsDrawer />
      <DeleteConfirmationModal
        title={`Delete “${selectedModuleTemplate?.name || ""}” Module`}
        content={generateTextForDeletingPopup(
          selectedModuleTemplate?.selectedGames?.length || 0,
        )}
        applyButtonTitle="Yes, delete"
        onClose={handleDeleteModal}
        open={openDeleteModal}
        handleSuccess={onDeleteModule}
        applyButtonLoading={isDeleteLoading}
      />
    </FlexColumn>
  );
};

export default ModulesContainer;
